import { useTranslation } from 'react-i18next';
import { LocalizedLink } from './LocalizedLink';
import { Mail, Phone, MapPin, Linkedin, Facebook, ArrowUpRight } from 'lucide-react';

export function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const productLinks = [
    { to: 'product_firmo', label: 'FirmoSEMPLICE' },
    { to: 'product_ai', label: 'SempliceAI' },
    { to: 'product_pa', label: 'SemplicePA' },
  ];

  const serviceLinks = [
    { to: 'service_dev', label: t('home.services.dev.title') },
    { to: 'service_integration', label: t('home.services.integration.title') },
    { to: 'service_security', label: t('home.services.security.title') },
    { to: 'service_cloud', label: t('home.services.cloud.title') },
  ];

  const companyLinks = [
    { to: 'home', label: t('nav.home') },
    { to: 'about', label: t('nav.about') },
    { to: 'realizzazioni', label: t('nav.realizzazioni') },
    { to: 'faq', label: 'FAQ' },
    { to: 'contacts', label: t('nav.contacts') },
  ];

  const legalLinks = [
    { to: 'privacy', label: t('footer.privacy') },
  ];

  return (
    <footer className="bg-gray-950 text-white relative overflow-hidden">
      {/* Gradient top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-400 to-transparent" />

      {/* Background decoration */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-400/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-400/5 rounded-full blur-3xl translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 relative">
        {/* Main Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column - spans 2 columns on large screens */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2 space-y-6">
            <LocalizedLink to="home" className="inline-block">
              <img
                src="/media/logos/spl-sistemi.png"
                alt="01Sistemi"
                className="h-10 w-auto brightness-0 invert"
              />
            </LocalizedLink>
            <p className="text-gray-400 text-sm leading-relaxed max-w-sm">
              {t('footer.description')}
            </p>

            {/* Social Links */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-3">
                {t('footer.followUs')}
              </p>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/01sistemi/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-primary-400 hover:text-white transition-all duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/01sistemi/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-primary-400 hover:text-white transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>
          </div>

          {/* Products Column */}
          <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
              {t('footer.productsTitle')}
            </h3>
            <ul className="space-y-3">
              {productLinks.map((link) => (
                <li key={link.to}>
                  <LocalizedLink
                    to={link.to}
                    className="text-gray-400 text-sm hover:text-white transition-colors duration-200 flex items-center gap-1 group"
                  >
                    {link.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column */}
          <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
              {t('footer.servicesTitle')}
            </h3>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.to}>
                  <LocalizedLink
                    to={link.to}
                    className="text-gray-400 text-sm hover:text-white transition-colors duration-200 flex items-center gap-1 group"
                  >
                    {link.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Company & Contact Column */}
          <div className="space-y-6">
            {/* Company Links */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                {t('footer.companyTitle')}
              </h3>
              <ul className="space-y-3">
                {companyLinks.map((link) => (
                  <li key={link.to}>
                    <LocalizedLink
                      to={link.to}
                      className="text-gray-400 text-sm hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Bar */}
        <div className="mt-16 pt-8 border-t border-gray-800/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="mailto:info@01s.it"
              className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center group-hover:bg-primary-400/20 transition-colors">
                <Mail className="w-4 h-4 text-primary-400" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider">{t('footer.email')}</div>
                <div className="text-sm">info@01s.it</div>
              </div>
            </a>

            <a
              href="tel:+3905751943310"
              className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center group-hover:bg-primary-400/20 transition-colors">
                <Phone className="w-4 h-4 text-primary-400" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider">{t('footer.contacts')}</div>
                <div className="text-sm">+39 0575 194 33 10</div>
              </div>
            </a>

            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-primary-400" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider">{t('footer.address')}</div>
                <div className="text-sm">Via Malpasso, 37 - Sansepolcro (AR)</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-800/50">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} 01Sistemi. {t('footer.allRightsReserved')}
            </p>
            <div className="flex items-center gap-6">
              {legalLinks.map((link) => (
                <LocalizedLink
                  key={link.to}
                  to={link.to}
                  className="text-gray-500 text-sm hover:text-gray-300 transition-colors"
                >
                  {link.label}
                </LocalizedLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
